import clsx from 'clsx';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { EXAMPLE_TYPES, ExampleTypeEnum } from '@/types/evals';

import styles from './modalMetricCreate.module.scss';
import { MetricFormData } from './schemas';

const OPTIONS: ExampleTypeEnum[] = [ EXAMPLE_TYPES.CONVERSATION, EXAMPLE_TYPES.MESSAGE ];

type Props = {
  disabled?: boolean;
};

/**
 * Multi-select example-type picker ("select all that apply"). A metric's
 * exampleType is an array, so — unlike the source modal's single-select
 * ExampleTypeSelect — this renders checkboxes. Toggles mark the form dirty so
 * the version flow's submit gate updates.
 */
export const ExampleTypesField: React.FC<Props> = ( { disabled } ) => {
  const { watch, setValue, formState: { errors } } = useFormContext<MetricFormData>();
  const selected = watch( 'exampleType' ) ?? [];
  const error = errors.exampleType as { message?: string } | undefined;

  const toggle = ( type: ExampleTypeEnum, checked: boolean ) => {
    const next = checked ? [ ...selected, type ] : selected.filter( ( t ) => t !== type );
    setValue( 'exampleType', next, { shouldDirty: true, shouldValidate: true } );
  };

  return (
    <div className={ clsx( 'layout__col', 'pad' ) } data-testid="example-types-field">
      <label className={ clsx( 'auth_flow_label', error && 'required' ) }>
        Example Types <span className={ styles.required_asterisk }>*</span> (select all that apply)
      </label>
      {OPTIONS.map( ( type ) => (
        <div className={ clsx( 'layout__row', 'flex_left' ) } key={ type }>
          <input
            id={ `example-type-${ type }` }
            type="checkbox"
            value={ type }
            checked={ selected.includes( type ) }
            disabled={ disabled }
            onChange={ ( e ) => toggle( type, e.currentTarget.checked ) }
            data-testid={ `example-type-${ type }-checkbox` }
          />
          <label htmlFor={ `example-type-${ type }` }>{type}</label>
        </div>
      ) )}
      {error && (
        <div className={ styles.error_message }>{error.message || 'Select at least one example type'}</div>
      )}
    </div>
  );
};
